import React from "react";

import { Box, Paper, Stack, Typography, Chip, Divider } from "@mui/material";

import TableViewIcon from "@mui/icons-material/TableView";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

// ─────────────────────────────────────────────────────────────
// Comparison Placeholder
// (Two Sides)
// ─────────────────────────────────────────────────────────────

export function ComparisonTablePlaceholder() {
  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 420,
        borderRadius: 4,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        p: 4,
      }}
    >
      <Stack spacing={3} alignItems="center" textAlign="center" maxWidth={760}>
        {/* Icon */}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            boxShadow: 4,
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 42 }} />
        </Box>

        {/* Title */}
        <Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Material Comparison Matrix
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 620,
              mx: "auto",
            }}
          >
            Configure both comparison sides, select source references and
            revisions, then choose audit metrics to analyze material variances
            between two datasets.
          </Typography>
        </Box>

        {/* Steps */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <StepCard
            icon={<AccountTreeIcon color="primary" />}
            title="1. Configure Side A & B"
            subtitle="Select lifecycle, source, and revision for both sides."
          />

          <StepCard
            icon={<AnalyticsIcon color="primary" />}
            title="2. Select Metrics"
            subtitle="Choose metrics for variance analysis."
          />

          <StepCard
            icon={<TableViewIcon color="primary" />}
            title="3. Compare Results"
            subtitle="Review validation status and differences."
          />
        </Stack>

        {/* Suggested Filters */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          justifyContent="center"
          useFlexGap
        >
          <Chip label="Lifecycle A" />
          <Chip label="Reference A" />
          <Chip label="Revision A" />
          <Chip label="Lifecycle B" />
          <Chip label="Reference B" />
          <Chip label="Revision B" />
          <Chip label="Audit Metrics" />
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────
// Single Audit Placeholder
// (One Side)
// ─────────────────────────────────────────────────────────────

export function SingleAuditTablePlaceholder() {
  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 420,
        borderRadius: 4,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        p: 4,
      }}
    >
      <Stack spacing={3} alignItems="center" textAlign="center" maxWidth={700}>
        {/* Icon */}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "primary.contrastText",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            boxShadow: 4,
          }}
        >
          <TableViewIcon sx={{ fontSize: 42 }} />
        </Box>

        {/* Title */}
        <Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Material Audit Matrix
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Select lifecycle configuration, reference source, revision, and
            audit metrics to generate a validation matrix for material auditing
            and engineering review.
          </Typography>
        </Box>

        {/* Steps */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <StepCard
            icon={<AccountTreeIcon color="primary" />}
            title="1. Configure Reference"
            subtitle="Select lifecycle, reference type, and revision."
          />

          <StepCard
            icon={<AnalyticsIcon color="primary" />}
            title="2. Select Metrics"
            subtitle="Choose audit metrics to display."
          />

          <StepCard
            icon={<TableViewIcon color="primary" />}
            title="3. Review Matrix"
            subtitle="Analyze material audit information."
          />
        </Stack>

        {/* Suggested Filters */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          justifyContent="center"
          useFlexGap
        >
          <Chip label="Lifecycle" />
          <Chip label="Reference Type" />
          <Chip label="Reference" />
          <Chip label="Revision" />
          <Chip label="Audit Metrics" />
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────
// Reusable Step Card
// ─────────────────────────────────────────────────────────────

function StepCard({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 180,
      }}
    >
      <Stack spacing={1} alignItems="center">
        {icon}

        <Typography fontWeight={700} fontSize="0.95rem">
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>
    </Box>
  );
}
